import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const prevPermsRef = useRef(null);

  const refreshUser = useCallback(async ({ silent = false } = {}) => {
    const token = localStorage.getItem("atlas_token");
    if (!token) return null;
    try {
      const me = await api.get("/auth/me");
      const fresh = me.data;
      // Detectar cambios en permisos para avisar al usuario y forzar refresh de la UI
      const prev = prevPermsRef.current;
      const prevSet = new Set(prev?.permissions || []);
      const nextSet = new Set(fresh?.permissions || []);
      const nuevos = [...nextSet].filter((p) => !prevSet.has(p));
      const removidos = [...prevSet].filter((p) => !nextSet.has(p));
      const cambioRol = prev && prev.role !== fresh?.role;
      const cambioActivo = prev && prev.active !== fresh?.active;
      const cambioNombre = prev && prev.name !== fresh?.name;
      if (prev && (nuevos.length || removidos.length || cambioRol || cambioActivo || cambioNombre) && !silent) {
        // Aviso amistoso: los cambios ya están aplicados.
        const partes = [];
        if (cambioNombre) partes.push(`nombre → ${fresh.name}`);
        if (cambioRol) partes.push(`rol → ${fresh.role}`);
        if (nuevos.length) partes.push(`+${nuevos.length} permiso${nuevos.length > 1 ? "s" : ""}`);
        if (removidos.length) partes.push(`-${removidos.length} permiso${removidos.length > 1 ? "s" : ""}`);
        toast.success("Tus datos fueron actualizados", {
          description: partes.join(" · ") || "Los cambios ya están activos.",
        });
        // También dispara un evento para que otros componentes se re-rendericen si es necesario
        try { window.dispatchEvent(new CustomEvent("perms-changed", { detail: { fresh, nuevos, removidos } })); } catch {}
      }
      // Si cambió el nombre (aunque sea silent al login), avisamos a las páginas
      // que muestran snapshots del nombre (`vendedor_nombre` en pedidos, etc.)
      // para que refetchen sus datos y no queden con el nombre antiguo.
      if (cambioNombre) {
        try { window.dispatchEvent(new CustomEvent("user-name-changed", { detail: { fresh } })); } catch {}
      }
      prevPermsRef.current = fresh;
      setUser(fresh);
      return fresh;
    } catch (e) {
      if (e?.response?.status === 401) localStorage.removeItem("atlas_token");
      return null;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("atlas_token");
    if (!token) { setLoading(false); return; }
    refreshUser({ silent: true }).finally(() => setLoading(false));
  }, [refreshUser]);

  // Auto-refresh de permisos:
  //  - cada 15s (polling ligero)
  //  - cuando el usuario vuelve a la pestaña (focus + visibilitychange)
  //  - cuando cualquier mutación exitosa dispara `notif-refresh` (interceptor de api.js)
  useEffect(() => {
    if (!user) return;
    const t = setInterval(() => { if (document.visibilityState === "visible") refreshUser(); }, 60000);
    const onFocus = () => refreshUser();
    const onVisibility = () => { if (!document.hidden) refreshUser(); };
    const onNotifRefresh = () => refreshUser();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("notif-refresh", onNotifRefresh);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("notif-refresh", onNotifRefresh);
    };
  }, [user, refreshUser]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("atlas_token", data.token);
    // Fetch complete user with permissions
    await refreshUser({ silent: true });
    // Notificar a la campanita que refresque de inmediato
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("notif-refresh"));
    }
    return data;
  };

  const register = async (payload) => {
    // payload: { email, password, name, telefono?, role_deseado? }
    const { data } = await api.post("/auth/register", payload);
    // Si es el primer usuario del sistema, el backend devuelve token y entra directo.
    // Si no, la cuenta queda pendiente y debe esperar aprobación del director.
    if (data?.pending) {
      return data; // { pending: true, message: "..." }
    }
    localStorage.setItem("atlas_token", data.token);
    await refreshUser({ silent: true });
    return data;
  };

  const logout = () => {
    localStorage.removeItem("atlas_token");
    prevPermsRef.current = null;
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

/**
 * Chequea si el usuario tiene un permiso especifico.
 * Directores (admin) o quien tenga permisos.administrar pasan por default.
 * Guardrail (Feb 9, 2026): un vendedor SIEMPRE conserva las capacidades
 * esenciales de su rol (crear pedidos, gestionar sus clientes, reportar pagos).
 * Idem para operador con despachos e inventario.
 */
const CORE_VENDEDOR = new Set([
  "ventas.crear", "ventas.editar",
  "clientes.ver", "clientes.crear", "clientes.editar",
  "pagos.reportar",
]);
const CORE_OPERADOR = new Set([
  "productos.editar", "productos.archivar", "inventario.ajustar",
  "despachos.tomar", "despachos.gestionar",
  "clientes.ver",
]);

export const useHasPerm = (perm) => {
  const { user } = useAuth();
  if (!user) return false;
  if (user.role === "admin") return true;
  const perms = user.permissions || [];
  if (perms.includes("permisos.administrar")) return true;
  if (perms.includes(perm)) return true;
  if (user.role === "vendedor" && CORE_VENDEDOR.has(perm)) return true;
  if (user.role === "operador" && CORE_OPERADOR.has(perm)) return true;
  return false;
};
